import React, { useEffect, useState } from "react";
import { Form, Input, Select, Button, Popconfirm, Upload, List, message } from "antd";
import { ThunderboltOutlined, PlusOutlined, DeleteOutlined, PaperClipOutlined, DownloadOutlined } from "@ant-design/icons";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import PageTransition from "../../components/ui/PageTransition";
import { Section } from "./employeeUi";
import { SECTIONS, COLUMNS, OPTIONS, buildPayload } from "./employeeSchema";
import { FieldItem } from "./EmployeeFields";

// Generates a strong random password with at least one of each character class.
const generatePassword = () => {
  const lower = "abcdefghijkmnopqrstuvwxyz";
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const digits = "23456789";
  const symbols = "!@#$%&*";
  const all = lower + upper + digits + symbols;
  const pick = (chars) => chars[Math.floor(Math.random() * chars.length)];
  const required = [pick(lower), pick(upper), pick(digits), pick(symbols)];
  const rest = Array.from({ length: 8 }, () => pick(all));
  return [...required, ...rest].sort(() => Math.random() - 0.5).join("");
};

const prettySize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

// One page for both "Add employee" and "Edit employee" (the id in the URL decides).
const EmployeeForm = () => {
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const { request } = useRequest();
  const [form] = Form.useForm();
  const [employee, setEmployee] = useState(null);
  const [lookups, setLookups] = useState({ departments: [], roles: [], offices: [], workingHours: [] });
  const [attachments, setAttachments] = useState([]);
  const [saving, setSaving] = useState(false);
  const salaryCurrency = Form.useWatch("salaryCurrency", form) || "PHP";

  useEffect(() => {
    const load = (key, url) => request({ url, method: "GET", onSuccess: (res) => setLookups((l) => ({ ...l, [key]: res?.data?.data || [] })) });
    load("departments", apiPath.departments);
    load("roles", apiPath.roles);
    load("offices", apiPath.offices);
    load("workingHours", apiPath.workingHours);
    if (editing) {
      request({
        url: `${apiPath.employees}/${id}`,
        method: "GET",
        onSuccess: (res) => {
          const e = res?.data;
          setEmployee(e);
          setAttachments(e.attachments || []);
          // older records only have a full name: split it so first / last name are filled in
          const [first, ...rest] = String(e.name || "").split(" ");
          form.setFieldsValue({
            ...e,
            firstName: e.firstName || first,
            lastName: e.lastName || rest.join(" "),
            department: e.department?._id,
            role: e.role?._id,
            assignedOffice: e.assignedOffice?._id,
            workingHours: e.workingHours?._id,
            joiningDate: e.joiningDate || "",
            region: e.region || undefined,
          });
        },
        onError: () => message.error("Could not load this employee"),
      });
    } else {
      form.setFieldsValue({ salaryCurrency: "PHP", payFrequency: "Monthly", attachmentAccess: "hidden", nextOfKin: [{}], employmentStatus: "active" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const cancel = () => navigate(editing ? `/employees/${id}` : "/employees");

  const onSave = () => {
    form
      .validateFields()
      .then((values) => {
        const payload = buildPayload(values, { editing });
        if (editing) payload.employmentStatus = values.employmentStatus || employee?.employmentStatus || "active";
        else {
          payload.email = values.email;
          payload.password = values.password;
        }
        setSaving(true);
        request({
          url: editing ? `${apiPath.employees}/${id}` : apiPath.employees,
          method: editing ? "PUT" : "POST",
          data: payload,
          onSuccess: (res) => {
            setSaving(false);
            message.success(editing ? "Employee updated" : "Employee created");
            navigate(editing ? `/employees/${id}` : res?.data?._id ? `/employees/${res.data._id}` : "/employees");
          },
          onError: (err) => {
            setSaving(false);
            message.error(err?.response?.data?.message || (editing ? "Update failed" : "Create failed"));
          },
        });
      })
      .catch(() => message.warning("Please fix the highlighted fields"));
  };

  const sendReset = () =>
    request({
      url: `${apiPath.employees}/${id}/send-reset-link`,
      method: "POST",
      onSuccess: () => message.success("Password reset link sent"),
      onError: (err) => message.error(err?.response?.data?.message || "Could not send the link"),
    });

  // ---- attachments: uploaded straight away (an employee must exist first) ----
  const uploadFile = ({ file, onSuccess, onError }) => {
    const data = new FormData();
    data.append("file", file);
    request({
      url: `${apiPath.employees}/${id}/attachments`,
      method: "POST",
      data,
      header: { "Content-Type": "multipart/form-data" },
      onSuccess: (res) => {
        setAttachments((a) => [...a, res.data]);
        message.success("Attachment added");
        onSuccess("ok");
      },
      onError: (err) => {
        message.error(err?.response?.data?.message || "Upload failed");
        onError(err);
      },
    });
  };
  const downloadFile = async (a) => {
    try {
      const token = localStorage.getItem("i_ems_admin_token") || "";
      const r = await axios.get(`${apiPath.baseURL}${apiPath.employees}/${id}/attachments/${a._id}/download`, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", a.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      message.error("Could not download the file");
    }
  };
  const removeFile = (a) =>
    request({
      url: `${apiPath.employees}/${id}/attachments/${a._id}`,
      method: "DELETE",
      onSuccess: () => setAttachments((list) => list.filter((x) => x._id !== a._id)),
      onError: () => message.error("Could not remove the file"),
    });

  // ---- sections drawn by hand ----
  const special = {
    account: (
      <>
        <Form.Item name="email" label="Work email" rules={[{ required: true, type: "email", message: "Enter a valid email" }]} extra="Used to sign in and for notifications">
          <Input disabled={editing} />
        </Form.Item>
        {editing ? (
          <>
            <Form.Item name="employmentStatus" label="Status">
              <Select options={[{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }]} />
            </Form.Item>
            <Popconfirm title="Email this employee a link to set a new password?" onConfirm={sendReset}>
              <Button>Reset password</Button>
            </Popconfirm>
          </>
        ) : (
          <Form.Item label="Password" required extra="This password (and a reset link) will be emailed to the employee." style={{ marginBottom: 0 }}>
            <Input.Group compact style={{ display: "flex" }}>
              <Form.Item name="password" rules={[{ required: true, min: 6, message: "At least 6 characters" }]} noStyle>
                <Input.Password style={{ flex: 1 }} placeholder="Set a password, or generate one" />
              </Form.Item>
              <Button icon={<ThunderboltOutlined />} onClick={() => form.setFieldsValue({ password: generatePassword() })}>
                Generate
              </Button>
            </Input.Group>
          </Form.Item>
        )}
      </>
    ),
    assets: (
      <Form.Item name="allocatedAssets" style={{ marginBottom: 0 }}>
        <Select mode="tags" style={{ width: "100%" }} placeholder="Type an asset and press Enter, e.g. Laptop, ID card" />
      </Form.Item>
    ),
    nextOfKin: (
      <Form.List name="nextOfKin">
        {(fields, { add, remove }) => (
          <>
            {fields.map((f, i) => (
              <div key={f.key} style={{ border: "1px solid #E5E7EB", borderRadius: 10, padding: "10px 12px 0", marginBottom: 10, position: "relative" }}>
                <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 6 }}>Contact {i + 1}</div>
                <Form.Item name={[f.name, "name"]} label="Name"><Input /></Form.Item>
                <Form.Item name={[f.name, "relationship"]} label="Relationship"><Input /></Form.Item>
                <Form.Item name={[f.name, "phone"]} label="Phone"><Input /></Form.Item>
                {fields.length > 1 || i > 0 ? <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => remove(f.name)} style={{ position: "absolute", top: 4, right: 6 }} /> : null}
              </div>
            ))}
            <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add({})}>
              Add contact
            </Button>
          </>
        )}
      </Form.List>
    ),
    customFields: (
      <Form.List name="customFields">
        {(fields, { add, remove }) => (
          <>
            {fields.map((f) => (
              <div key={f.key} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                <Form.Item name={[f.name, "label"]} style={{ flex: 1 }}><Input placeholder="Label" /></Form.Item>
                <Form.Item name={[f.name, "value"]} style={{ flex: 1 }}><Input placeholder="Value" /></Form.Item>
                <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(f.name)} />
              </div>
            ))}
            <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add({})}>
              Add a custom field
            </Button>
          </>
        )}
      </Form.List>
    ),
    attachments: editing ? (
      <>
        <Form.Item name="attachmentAccess" label="Employee access"><Select options={OPTIONS.attachmentAccess} /></Form.Item>
        <List
          size="small"
          locale={{ emptyText: "No files yet" }}
          dataSource={attachments}
          renderItem={(a) => (
            <List.Item
              actions={[
                <Button key="d" type="text" size="small" icon={<DownloadOutlined />} onClick={() => downloadFile(a)} />,
                <Popconfirm key="r" title="Remove this file?" onConfirm={() => removeFile(a)}>
                  <Button type="text" danger size="small" icon={<DeleteOutlined />} />
                </Popconfirm>,
              ]}
            >
              <List.Item.Meta title={<span style={{ fontSize: 13 }}>{a.name}</span>} description={`${prettySize(a.size)} · ${moment(a.uploadedAt).format("DD MMM YYYY")}`} />
            </List.Item>
          )}
        />
        <Upload customRequest={uploadFile} showUploadList={false} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx">
          <Button icon={<PaperClipOutlined />} style={{ marginTop: 8 }}>
            Add file
          </Button>
        </Upload>
        <div style={{ color: "#9CA3AF", fontSize: 12, marginTop: 6 }}>PDF, image or Word, up to 10 MB each.</div>
      </>
    ) : (
      <div style={{ color: "#6B7280" }}>Save the employee first, then add files here.</div>
    ),
  };

  const renderSection = (key) => {
    const section = SECTIONS[key];
    return (
      <Section key={key} id={key} title={section.title} hint={section.note}>
        {section.special ? (
          special[key]
        ) : (
          section.fields
            .filter((f) => !(f.editOnly && !editing))
            .map((f) => <FieldItem key={f.path.join(".")} field={f} lookups={lookups} region={salaryCurrency} />)
        )}
      </Section>
    );
  };

  return (
    <PageTransition>
      <div className="emp-form-bar">
        <div style={{ color: "#6B7280", fontSize: 13 }}>
          <Link to="/employees">Employees</Link> ›{" "}
          {editing ? (
            <>
              <Link to={`/employees/${id}`}>{employee?.name || "Employee"}</Link> › <b style={{ color: "#111827" }}>Edit</b>
            </>
          ) : (
            <b style={{ color: "#111827" }}>Add employee</b>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap", marginTop: 4 }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <h1 style={{ fontSize: 22, margin: 0 }}>{editing ? "Edit employee" : "Add employee"}</h1>
            <div style={{ color: "#6B7280" }}>{editing ? `${employee?.name || ""}${employee ? ` · ${employee.employeeId}` : ""}` : "Create a new employee account"}</div>
          </div>
          <Button onClick={cancel}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={onSave}>
            {editing ? "Save changes" : "Create employee"}
          </Button>
        </div>
      </div>

      <Form layout="vertical" form={form} requiredMark>
        <div className="emp-cols">
          {COLUMNS.map((col, i) => (
            <div className="emp-col" key={i}>
              {col.map(renderSection)}
            </div>
          ))}
        </div>
      </Form>
    </PageTransition>
  );
};

export default EmployeeForm;
